// =============================================================================
// FILE PATH : src/modules/pms/wbs_template/components/WBSTemplateDrawer.js
// MODULE    : PMS → WBS Template Integration
// PURPOSE   : Drawer to select and apply templates to WBS (Project / Item mode)
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";

import {
  getTemplateLibrary,
  applyTemplateToWBS
} from "../services/wbsTemplateService";

import TemplateGroupList from "./TemplateGroupList";
import TemplateFilterBar from "./TemplateFilterBar";


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.3)",
  zIndex: 1000
};

const drawerStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  width: "700px",
  height: "100%",
  background: "#fff",
  zIndex: 1001,
  display: "flex",
  flexDirection: "column"
};

const headerStyle = {
  padding: "16px",
  borderBottom: "1px solid #eee",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
};

const bodyStyle = {
  flex: 1,
  overflowY: "auto"
};

const footerStyle = {
  padding: "12px",
  borderTop: "1px solid #eee",
  display: "flex",
  justifyContent: "space-between"
};


// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------
const WBSTemplateDrawer = ({
  open,
  onClose,
  projectId,
  selectedNode,
  onSuccess
}) => {

  // ---------------------------------------------------------------------------
  // STATES
  // ---------------------------------------------------------------------------
  const [library, setLibrary] = useState({
    project_templates: [],
    equipment_templates: []
  });

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔥 MAXIMIZE / RESTORE
  const [isMaximized, setIsMaximized] = useState(false);

  // 🔥 SEARCH + FILTER + SORT
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("NAME");

  // ---------------------------------------------------------------------------
  // MODE DETECTION
  // ---------------------------------------------------------------------------
  //const mode = !selectedNode
  //  ? "PROJECT"
  //  : selectedNode.type === "PHASE"
  //  ? "ITEM"
  //  : "INVALID";
    const mode = !selectedNode
    ? "PROJECT"
    : selectedNode.type === "ITEM"
    ? "ITEM"
    : selectedNode.type === "PHASE"
    ? "PROJECT"
    : "INVALID";
  // ---------------------------------------------------------------------------
  // DYNAMIC DRAWER STYLE
  // ---------------------------------------------------------------------------
  const computedDrawerStyle = {
    ...drawerStyle,
    width: isMaximized ? "100%" : "700px",
    left: isMaximized ? 0 : "auto",
    right: 0,
    transition: "all 0.3s ease"
  };

  // ---------------------------------------------------------------------------
  // FETCH LIBRARY
  // ---------------------------------------------------------------------------
  useEffect(() => {

    if (!open) return;

    setSelectedTasks([]);

    const fetchLibrary = async () => {

      try {

        setLoading(true);

        const res = await getTemplateLibrary();

        setLibrary(res.data || {});

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);
      }
    };

    fetchLibrary();

  }, [open]);

  // ---------------------------------------------------------------------------
  // HANDLE SELECT
  // ---------------------------------------------------------------------------
    const toggleTask = (
    task,
    templateName,
    templateType
  ) => {

    const enrichedTask = {
      ...task,

      // 🔥 IMPORTANT
      template_name: templateName,
      template_type: templateType
    };

    const exists = selectedTasks.find(
      (t) =>
        t.task_name === task.task_name &&
        t.template_name === templateName
    );

    if (exists) {

      setSelectedTasks(
        selectedTasks.filter(
          (t) =>
            !(
              t.task_name === task.task_name &&
              t.template_name === templateName
            )
        )
      );

    } else {

      setSelectedTasks([
        ...selectedTasks,
        enrichedTask
      ]);
    }
  };

  // ---------------------------------------------------------------------------
  // APPLY TEMPLATE
  // ---------------------------------------------------------------------------
  const handleApply = async () => {

    if (!selectedTasks.length) {

      alert("Select at least one task");

      return;
    }

    try {

      await applyTemplateToWBS({
      project_id: projectId,
      parent_id: selectedNode?.id || null,

      // 🔥 TAKE FROM FIRST SELECTED TASK
      template_name:
        selectedTasks[0]?.template_name,

      template_type:
        selectedTasks[0]?.template_type,

      tasks: selectedTasks
    });

      onSuccess();

      onClose();

    } catch (err) {

      console.error(err);

      alert("Error applying template");
    }
  };

  // ---------------------------------------------------------------------------
  // FILTER DATA BASED ON MODE
  // ---------------------------------------------------------------------------
  const getModeTemplates = () => {

    // -------------------------------------------------------------------------
    // PROJECT MODE
    // -------------------------------------------------------------------------
    if (mode === "PROJECT") {
      return library.project_templates || [];
    }

    // -------------------------------------------------------------------------
    // ITEM MODE
    // -------------------------------------------------------------------------
    if (mode === "ITEM") {

      // TEMPORARY:
      // Showing all templates until order item integration

      return library.equipment_templates || [];
    }

    return [];
  };

  // ---------------------------------------------------------------------------
  // SMART SEARCH + FILTER + SORT
  // ---------------------------------------------------------------------------
  const templates = useMemo(() => {

    const rawTemplates = getModeTemplates();

    const search = searchText.toLowerCase().trim();

    let filtered = [];

    // -------------------------------------------------------------------------
    // PROJECT MODE
    // -------------------------------------------------------------------------
    if (mode === "PROJECT") {

      filtered = rawTemplates
        .map((template) => {

          const matchedTasks = (template.tasks || []).filter((task) => {

            const taskName =
              (task.task_name || "").toLowerCase();

            const taskCategory =
              (task.task_category || "").toLowerCase();

            const templateName =
              (template.template_name || "").toLowerCase();

            const searchMatch =
              !search ||
              taskName.includes(search) ||
              taskCategory.includes(search) ||
              templateName.includes(search);

            const categoryMatch =
              categoryFilter === "ALL" ||
              task.task_category === categoryFilter;

            return searchMatch && categoryMatch;
          });

          if (
            matchedTasks.length ||
            (!search &&
              categoryFilter === "ALL")
          ) {

            return {
              ...template,
              tasks: matchedTasks.length
                ? matchedTasks
                : template.tasks
            };
          }

          return null;

        })
        .filter(Boolean);
    }

    // -------------------------------------------------------------------------
    // ITEM MODE
    // -------------------------------------------------------------------------
    if (mode === "ITEM") {

      filtered = rawTemplates
        .map((equipmentGroup) => {

          const equipmentName =
            (equipmentGroup.equipment_name || "")
              .toLowerCase();

          const equipmentCategory =
            (equipmentGroup.equipment_category || "")
              .toLowerCase();

          const productVariant =
            (equipmentGroup.product_variant_name || "")
              .toLowerCase();

          const templates = (equipmentGroup.templates || [])
            .map((template) => {

              const templateName =
                (template.template_name || "")
                  .toLowerCase();

              const matchedTasks =
                (template.tasks || []).filter((task) => {

                  const taskName =
                    (task.task_name || "")
                      .toLowerCase();

                  const taskCategory =
                    (task.task_category || "")
                      .toLowerCase();

                  const searchMatch =
                    !search ||
                    taskName.includes(search) ||
                    templateName.includes(search) ||
                    equipmentName.includes(search) ||
                    equipmentCategory.includes(search) ||
                    productVariant.includes(search) ||
                    taskCategory.includes(search);

                  const categoryMatch =
                    categoryFilter === "ALL" ||
                    task.task_category === categoryFilter;

                  return searchMatch && categoryMatch;
                });

              if (
                matchedTasks.length ||
                (!search &&
                  categoryFilter === "ALL")
              ) {

                return {
                  ...template,
                  tasks: matchedTasks.length
                    ? matchedTasks
                    : template.tasks
                };
              }

              return null;
            })
            .filter(Boolean);

          if (templates.length) {

            return {
              ...equipmentGroup,
              templates
            };
          }

          return null;

        })
        .filter(Boolean);
    }

    // -------------------------------------------------------------------------
    // SORT
    // -------------------------------------------------------------------------
    if (sortBy === "NAME") {

      filtered.sort((a, b) => {

        const aName =
          (a.template_name || a.equipment_name || "");

        const bName =
          (b.template_name || b.equipment_name || "");

        return aName.localeCompare(bName);
      });
    }

    return filtered;

  }, [
    library,
    mode,
    searchText,
    categoryFilter,
    sortBy
  ]);

  // ---------------------------------------------------------------------------
  // NOT OPEN
  // ---------------------------------------------------------------------------
  if (!open) return null;

  // ---------------------------------------------------------------------------
  // INVALID MODE
  // ---------------------------------------------------------------------------
  if (mode === "INVALID") {

    return (
      <>
        <div
          style={overlayStyle}
          onClick={onClose}
        ></div>

        <div style={computedDrawerStyle}>

          <div style={headerStyle}>
            <b>Invalid Selection</b>

            <button onClick={onClose}>
              ✖
            </button>
          </div>

          <div style={{
            padding: "16px"
          }}>
            Please select an ITEM node or deselect to apply project template.
          </div>

        </div>
      </>
    );
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <>
      <div
        style={overlayStyle}
        onClick={onClose}
      ></div>

      <div style={computedDrawerStyle}>

        {/* HEADER */}
        <div style={headerStyle}>

          <div>

            <div style={{
              fontWeight: "600",
              fontSize: "16px"
            }}>
              Add from Template
            </div>

            <div style={{
              fontSize: "12px",
              color: "#666",
              marginTop: "4px"
            }}>
              Mode: {mode}
            </div>

            {selectedNode && (
              <div style={{
                fontSize: "12px",
                color: "#2563eb",
                marginTop: "4px"
              }}>
                Selected Node: {selectedNode.name}
              </div>
            )}

            {/* TEMP WARNING */}
            {mode === "ITEM" && (
              <div style={{
                fontSize: "11px",
                color: "#d97706",
                marginTop: "4px"
              }}>
                Showing all templates until Order Item integration is enabled
              </div>
            )}

          </div>

          {/* ACTION BUTTONS */}
          <div>

            <button
              onClick={() => setIsMaximized(!isMaximized)}
              style={{
                marginRight: "8px",
                padding: "6px 10px",
                cursor: "pointer"
              }}
            >
              {isMaximized
                ? "🗗 Restore"
                : "🗖 Maximize"}
            </button>

            <button
              onClick={onClose}
              style={{
                padding: "6px 10px",
                cursor: "pointer"
              }}
            >
              ✖
            </button>

          </div>

        </div>

        {/* FILTER BAR */}
        <TemplateFilterBar
          searchText={searchText}
          onSearchChange={setSearchText}

          categoryFilter={categoryFilter}
          onCategoryChange={setCategoryFilter}

          sortBy={sortBy}
          onSortChange={setSortBy}
        />

        {/* BODY */}
        <div style={bodyStyle}>

          {loading && (
            <div style={{
              padding: "16px"
            }}>
              Loading...
            </div>
          )}

          {!loading && (
            <TemplateGroupList
              mode={mode}
              templates={templates}
              selectedTasks={selectedTasks}
              onToggle={toggleTask}
            />
          )}

          {!loading && !templates.length && (
            <div style={{
              padding: "16px",
              color: "#666"
            }}>
              No templates found
            </div>
          )}

        </div>

        {/* FOOTER */}
        <div style={footerStyle}>

          <div>
            Selected: {selectedTasks.length}
          </div>

          <div>

            <button
              onClick={onClose}
              style={{
                marginRight: "8px"
              }}
            >
              Cancel
            </button>

            <button
              onClick={handleApply}
            >
              Add Selected
            </button>

          </div>

        </div>

      </div>
    </>
  );
};

export default WBSTemplateDrawer;